export interface CalendarEvent {
  title: string;
  organiser: string;
  date: string;
  endDate?: string | null;
  location?: string | null;
  description?: string | null;
  url: string;
}

export interface CalendarLinks {
  googleUrl: string;
  icsBlob: Blob;
}

function toIcsDate(iso: string): string {
  return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function toGcalDate(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function defaultEnd(startIso: string): string {
  return new Date(new Date(startIso).getTime() + 3_600_000).toISOString();
}

function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

export function buildGoogleCalendarUrl(event: CalendarEvent): string {
  const start = toGcalDate(event.date);
  const end = event.endDate ? toGcalDate(event.endDate) : toGcalDate(defaultEnd(event.date));
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${start}/${end}`,
    location: event.location ?? "Online",
    details: `${event.description ?? ""}\n\nMore info: ${event.url}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function buildIcsContent(event: CalendarEvent): string {
  const start = toIcsDate(new Date(event.date).toISOString());
  const end = event.endDate
    ? toIcsDate(new Date(event.endDate).toISOString())
    : toIcsDate(new Date(defaultEnd(event.date)).toISOString());
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@rheumhub`;
  const desc = escapeIcsText(event.description ?? "");
  const loc = escapeIcsText(event.location ?? "Online");
  const summary = escapeIcsText(event.title);

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//RheumHub//Events//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${summary}`,
    `ORGANIZER;CN=${escapeIcsText(event.organiser)}:mailto:noreply@medantixhealth.co.uk`,
    `LOCATION:${loc}`,
    `DESCRIPTION:${desc}\\n\\nMore info: ${event.url}`,
    `URL:${event.url}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function buildCalendarLinks(event: CalendarEvent): CalendarLinks {
  const googleUrl = buildGoogleCalendarUrl(event);
  const icsBlob = new Blob([buildIcsContent(event)], { type: "text/calendar;charset=utf-8" });
  return { googleUrl, icsBlob };
}

export function triggerIcsDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
